

/* global google */
import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { gmApiKey } from '../../parseConfig';

import {
  MarkerClusterer,
  MarkerClustererEvents,
  SuperClusterAlgorithm,
  SuperClusterViewportAlgorithm,
} from "@googlemaps/markerclusterer";

import TopButtonsStock from './UITopButtonsStock';

import useUIPresentationMode from '../helpers/useUIPresentationMode.js';

import { fetchParcelBoundariesLayer, fetchAssociatedLayers } from '../helpers/fetchMapLayersStockMap';
import { getControlledMapTypeId } from '../helpers/mapOptions';
import {
  formatMapViewportForUrl,
  readMapViewportFromSearch,
} from '../helpers/mapViewportUrlState';
import { replaceUrlState } from '../helpers/urlUpdaterHelper';
import { USAGE_CLUSTER_RADIUS, usageClusterRenderer } from '../helpers/usageMarkerClustering';
import { releaseSupersededClusterMarkers } from './releaseClusterMarkers';

import fraemohsMarker from '../../images/FraemohsMarker@3x.png';
import fraemohsMarkerSelected from '../../images/FraemohsMarkerSelected@3x.png';


const GMAPS_LIBRARIES = ['places', 'marker'];

const containerStyle = {
  width: '100%',
  height: '100%',
};

const defaultNZ = { lat: -41.0, lng: 173.0 }; // centre of NZ


export function fitClusterBoundsWithPadding(map, cluster, padding) {
  if (!cluster?.bounds) return;
  map.fitBounds(cluster.bounds, padding);
}

export function shouldApplyMarkerFocusZoom(currentZoom, zoomThreshold, forceZoom = false) {
  return forceZoom || currentZoom <= zoomThreshold;
}


function StockMapComponent({
  listings = [],
  listingCount,
  setSelectedListing,
  presentationModeiFrame,
  onForceRightSidebarOpen,
  enableLayerPicker = false,
  layers = { primary: [], secondary: [] },
  selectedBaseMap = 'None',
  isParcelBoundariesVisible = true,
  clusterStyle = 'stock',
  clusterClickPadding = null,
  viewportClustering = false,
  minimumZoom = 6,
  satelliteMapTypeId = 'satellite',
  persistViewportInUrl = false,
  markerFocusZoomThreshold = 16,
  markerFocusZoom = 19,
}, ref) {

  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const initialUrlViewport = useMemo(() => (
    persistViewportInUrl
      ? readMapViewportFromSearch(window.location.search)
      : null
  ), [persistViewportInUrl]);
  const viewportPersistenceReadyRef = useRef(Boolean(initialUrlViewport));

  const { presentationModeSize } = useUIPresentationMode();

  const didInitialFit = useRef(false);

  const lastSelectedMarkerIconRef = useRef(null);
  const markerIconsByListingIdRef = useRef(new Map());

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps',
    googleMapsApiKey: gmApiKey,
    libraries: GMAPS_LIBRARIES,
  });

  const focusListing = useCallback((listing, markerIcon = null, forceZoom = false) => {
    if (!mapRef.current || !listing?.coordinates) return false;

    if (lastSelectedMarkerIconRef.current) {
      try {
        lastSelectedMarkerIconRef.current.src = fraemohsMarker;
      } catch (error) {
        console.warn("Previous marker icon not available anymore", error);
      }
    }

    const nextMarkerIcon = markerIcon
      || markerIconsByListingIdRef.current.get(listing.id || listing.objectId);
    if (nextMarkerIcon) {
      nextMarkerIcon.src = fraemohsMarkerSelected;
      lastSelectedMarkerIconRef.current = nextMarkerIcon;
    } else {
      lastSelectedMarkerIconRef.current = null;
    }

    setSelectedListing(listing);

    const currentZoom = mapRef.current.getZoom();
    if (shouldApplyMarkerFocusZoom(currentZoom, markerFocusZoomThreshold, forceZoom)) {
      mapRef.current.setCenter(listing.coordinates);
      mapRef.current.setZoom(markerFocusZoom);
    }

    if (typeof onForceRightSidebarOpen === "function") {
      onForceRightSidebarOpen();
    }

    return true;
  }, [markerFocusZoom, markerFocusZoomThreshold, onForceRightSidebarOpen, setSelectedListing]);

  // Determine appropriate padding based on presentation mode.
  const getMapPadding = useCallback(() => {
    let padding = 100;

    if (presentationModeSize === 1 || presentationModeSize === 2) {  // Big
      padding = 210;
    } else if (presentationModeSize === 3) {  // Medium
      padding = 210;
    } else if (presentationModeSize === 4 || presentationModeSize === 5) {  // Little
      padding = 40;
    }

    return { top: padding, right: padding, bottom: padding, left: padding };
  }, [presentationModeSize]);


  // Fit the map to all markers
  const zoomToAllMarkers = useCallback((listings) => {

    if (!mapRef.current || !listings.length) return;

    const bounds = new google.maps.LatLngBounds();

    listings
      .filter((l) => l.coordinates)
      .forEach((l) => bounds.extend(l.coordinates));

    if (bounds.isEmpty()) return;

    mapRef.current.fitBounds(bounds, getMapPadding());  // Scale this mofo depending on screen size.

  }, [getMapPadding]);

  const handleZoomToAll = useCallback(() => {
    if (listings.length) {
      zoomToAllMarkers(listings);
    }
  }, [listings, zoomToAllMarkers]);


  const persistCurrentViewport = useCallback(() => {
    if (
      !persistViewportInUrl
      || !viewportPersistenceReadyRef.current
      || !mapRef.current
    ) {
      return;
    }

    const center = mapRef.current.getCenter();
    const zoom = mapRef.current.getZoom();

    if (!center || !Number.isFinite(zoom)) return;

    const nextViewport = formatMapViewportForUrl({
      lat: center.lat(),
      lng: center.lng(),
      zoom,
    });
    const url = new URL(window.location.href);
    const viewportIsUnchanged = Object.entries(nextViewport).every(
      ([key, value]) => url.searchParams.get(key) === value
    );

    if (viewportIsUnchanged) return;

    Object.entries(nextViewport).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    replaceUrlState(url);
  }, [persistViewportInUrl]);


  /*-----------------------------------------------------------------------------*/


  useEffect(() => {

    if (!isLoaded || !mapReady || !listings.length) {
      return;
    }

    const markerClickCleanups = [];
    const markerIconsByListingId = markerIconsByListingIdRef.current;
    const markers = listings

      .filter((listing) => listing.coordinates)

      .map((listing) => {

        const markerIcon = document.createElement("img");
        markerIcon.src = fraemohsMarker;
        markerIcon.style.width = "32px";
        markerIcon.style.height = "32px";
        markerIcon.style.transform = 'translateY(50%)';
        markerIcon.style.borderRadius = '50%';
        markerIcon.style.boxShadow = '2px 2px 8px rgba(0,0,0,0.8)';

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: listing.coordinates,
          title: listing.displayTitle,
          content: markerIcon,
          gmpClickable: true,
        });
        const listingId = listing.id || listing.objectId;
        if (listingId) markerIconsByListingId.set(listingId, markerIcon);

        const handleMarkerClick = () => {
          focusListing(listing, markerIcon);
        };
        marker.addEventListener('gmp-click', handleMarkerClick);
        markerClickCleanups.push(() => {
          marker.removeEventListener('gmp-click', handleMarkerClick);
          if (listingId && markerIconsByListingId.get(listingId) === markerIcon) {
            markerIconsByListingId.delete(listingId);
          }
        });

        return marker;
      });

    // Create the clusterer with inline renderer option
    const useUsageClustering = clusterStyle === 'usage';
    const algorithmOptions = {
      radius: useUsageClustering ? USAGE_CLUSTER_RADIUS : 80,
    };
    const clusterer = new MarkerClusterer({

      map: mapRef.current,
      markers,
      algorithm: viewportClustering
        ? new SuperClusterViewportAlgorithm({
          ...algorithmOptions,
          viewportPadding: 100,
        })
        : new SuperClusterAlgorithm(algorithmOptions),
      renderer: useUsageClustering ? usageClusterRenderer : {
        render: ({ count, position }) => {
          const svg = window.btoa(`
        <svg fill="#596A99" xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <circle cx="20" cy="20" r="18" fill="#1D2946" opacity="0.9" />
          <text x="20" y="26" text-anchor="middle" font-size="15px" font-family="Arial" fill="#fff">${count}</text>
        </svg>
      `);
          const img = document.createElement("img");
          img.src = `data:image/svg+xml;base64,${svg}`;
          img.width = 40;
          img.height = 40;

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: img,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        },
      },
      ...(useUsageClustering && clusterClickPadding !== null
        ? {
          onClusterClick: (event, cluster, map) => {
            fitClusterBoundsWithPadding(map, cluster, clusterClickPadding);
          },
        }
        : {}),
    });

    // Add custom click behaviour for cluster icons
    if (!useUsageClustering) {
      clusterer.onClusterClick = (event, cluster, map) => {
        // Get all positions in this cluster
        const bounds = new google.maps.LatLngBounds();
        cluster.markers.forEach((marker) => {
          bounds.extend(marker.position);
        });

        map.fitBounds(bounds, getMapPadding());  // Scale this mofo depending on screen size.
      };
    }

    let previousClusters = [];
    const clusteringBeginListener = clusterer.addListener(
      MarkerClustererEvents.CLUSTERING_BEGIN,
      () => {
        previousClusters = [...clusterer.clusters];
      },
    );
    const clusteringEndListener = clusterer.addListener(
      MarkerClustererEvents.CLUSTERING_END,
      () => {
        releaseSupersededClusterMarkers(previousClusters, clusterer.clusters);
        previousClusters = [];
      },
    );

    // Cleanup: remove markers when listings change/unmount
    return () => {
      clusteringBeginListener.remove();
      clusteringEndListener.remove();
      markerClickCleanups.forEach((cleanup) => cleanup());
      releaseSupersededClusterMarkers(clusterer.clusters, []);
      clusterer.setMap(null);
      clusterer.clearMarkers(true);
      lastSelectedMarkerIconRef.current = null;
      markerIconsByListingId.clear();
    };

    // Important: only use these dependencies so markers aren't rebuilt on sidebar/UI changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapReady, listings]);


  // When the map and the listings are ready, do a single initial fit.
  useEffect(() => {
    if (!mapRef.current || !mapReady || !listings.length || didInitialFit.current) return;
    didInitialFit.current = true;

    if (initialUrlViewport) {
      viewportPersistenceReadyRef.current = true;
      return;
    }

    let tilesLoadedListener;

    // Let layout settle, then fit.
    const animationFrame = requestAnimationFrame(() => {
      zoomToAllMarkers(listings);
      viewportPersistenceReadyRef.current = true;

      // One-time safety re-fit after visible tiles load.
      tilesLoadedListener = mapRef.current?.addListener('tilesloaded', () => {
        tilesLoadedListener.remove();
        zoomToAllMarkers(listings);
      });
    });

    return () => {
      cancelAnimationFrame(animationFrame);
      tilesLoadedListener?.remove();
    };
  }, [initialUrlViewport, mapReady, listings, zoomToAllMarkers]);


  // Set up the map layers.
  // Add parcel boundaries once
  useEffect(() => {
    if (enableLayerPicker || !mapRef.current || !mapReady) return;

    (async () => {
      try {
        const parcelBoundaryUrl = await fetchParcelBoundariesLayer();
        if (parcelBoundaryUrl) {
          const parcelLayer = new window.google.maps.ImageMapType({
            getTileUrl: (coord, zoom) =>
              parcelBoundaryUrl
                .replace('{z}', zoom)
                .replace('{x}', coord.x)
                .replace('{y}', coord.y),
            tileSize: new window.google.maps.Size(256, 256),
            maxZoom: 22,
            name: 'Parcel Boundaries',
            opacity: 0.4,
          });
          mapRef.current.overlayMapTypes.insertAt(0, parcelLayer);
        }
      } catch (err) {
        console.error("Failed to init parcel boundaries:", err);
      }
    })();
  }, [enableLayerPicker, mapReady]);


  // Update associated layers whenever listings change
  useEffect(() => {
    if (enableLayerPicker || !mapRef.current || !mapReady) return;

    (async () => {
      try {
        // Remove all overlays after the parcel boundaries (index 0)
        while (mapRef.current.overlayMapTypes.getLength() > 1) {
          mapRef.current.overlayMapTypes.removeAt(1);
        }

        const associatedIds = [
          ...new Set(listings.map((l) => l.associatedMapLayerId).filter(Boolean)),
        ];

        if (associatedIds.length > 0) {
          const associatedUrls = await fetchAssociatedLayers(associatedIds);

          Object.entries(associatedUrls).forEach(([id, url]) => {
            const overlay = new window.google.maps.ImageMapType({
              getTileUrl: (coord, zoom) =>
                url
                  .replace('{z}', zoom)
                  .replace('{x}', coord.x)
                  .replace('{y}', coord.y),
              tileSize: new window.google.maps.Size(256, 256),
              maxZoom: 22,
              opacity: 0.75,
              name: `Layer ${id}`,
            });
            mapRef.current.overlayMapTypes.push(overlay);
          });
        }

        // Re-assert parcel opacity each update
        const parcelLayer = mapRef.current.overlayMapTypes.getAt(0);
        if (parcelLayer) parcelLayer.opacity = 0.4;

      } catch (err) {
        console.error("Failed to update associated layers:", err);
      }
    })();
  }, [enableLayerPicker, listings, mapReady]);


  // Usage-style selectable base maps and parcel-boundary toggle. This is
  // opt-in so the existing stock map keeps its current automatic layers.
  useEffect(() => {
    if (!enableLayerPicker || !mapRef.current || !mapReady) return;

    const map = mapRef.current;
    map.overlayMapTypes.clear();

    const selectedLayer = Array.isArray(layers.secondary)
      ? layers.secondary.find((layer) => layer.get('name') === selectedBaseMap)
      : null;

    if (selectedBaseMap === 'None') {
      map.setMapTypeId('roadmap');
    } else if (selectedLayer?.get('format')) {
      map.setMapTypeId('roadmap');
      map.overlayMapTypes.push(new window.google.maps.ImageMapType({
        getTileUrl: (coord, zoom) => selectedLayer.get('format')
          .replace('{z}', zoom)
          .replace('{x}', coord.x)
          .replace('{y}', coord.y),
        tileSize: new window.google.maps.Size(256, 256),
        maxZoom: 22,
        name: selectedLayer.get('name'),
      }));
    } else if (selectedBaseMap === 'Google Maps Satellite') {
      map.setMapTypeId(satelliteMapTypeId);
    } else {
      map.setMapTypeId('roadmap');
    }

    const parcelLayer = Array.isArray(layers.primary) ? layers.primary[0] : null;
    if (isParcelBoundariesVisible && parcelLayer?.get('format')) {
      map.overlayMapTypes.push(new window.google.maps.ImageMapType({
        getTileUrl: (coord, zoom) => parcelLayer.get('format')
          .replace('{z}', zoom)
          .replace('{x}', coord.x)
          .replace('{y}', coord.y),
        tileSize: new window.google.maps.Size(256, 256),
        maxZoom: 22,
        name: parcelLayer.get('name'),
        opacity: 0.4,
      }));
    }
  }, [enableLayerPicker, isParcelBoundariesVisible, layers.primary, layers.secondary, mapReady, satelliteMapTypeId, selectedBaseMap]);


  // Helper to reset selected marker icon
  const clearMarkerSelection = useCallback(() => {

    if (lastSelectedMarkerIconRef.current) {

      try {
        lastSelectedMarkerIconRef.current.src = fraemohsMarker;
      } catch (e) {
        console.warn("Marker reset failed", e);
      }
      lastSelectedMarkerIconRef.current = null;
    }

  }, []);


  useImperativeHandle(ref, () => ({
    clearMarkerSelection,
    focusListing: (listing) => focusListing(listing, null, true),
    zoomToAll: () => handleZoomToAll(),
  }), [clearMarkerSelection, focusListing, handleZoomToAll]);

  // Keep one stable options object across marker/sidebar renders. Recreating it
  // used to reapply `roadmap` and silently replace the selected Built base map.
  const options = useMemo(() => ({
    mapId: 'ed3c51d7a4dfee624cd8272f',  // Set this in https://console.cloud.google.com/google-maps-apis -> map management. Needed to use the new marker type.
    mapTypeId: getControlledMapTypeId({
      enableLayerPicker,
      selectedBaseMap,
      satelliteMapTypeId,
    }),
    //styles: washedOutMapStyle,  // Apply custom washed out style to the map. Note additional controls at the end of washedOutMapStyle.js. This is done in the web console now (https://console.cloud.google.com/google/maps-apis).
    maxZoom: 21,  // Increase max zoom level.
    ...(Number.isFinite(minimumZoom) && { minZoom: minimumZoom }),
    tilt: 0,                    // No tilt.
    //rotateControl: false,       // Disable rotate control.
    //mapTypeControl: false,      // Disable map type control.
    //fullscreenControl: false,   // Disable full screen control.
    //streetViewControl: false,   // Disable street view control.
    disableDefaultUI: true,       // Catch-all setting for the normal GM UI.
    clickableIcons: false,        // Make points of interest unclickable.
    ...(presentationModeiFrame && { gestureHandling: 'greedy' })  // Prevents "use two fingers to move the map" when presented in an iframe.
  }), [
    enableLayerPicker,
    minimumZoom,
    presentationModeiFrame,
    satelliteMapTypeId,
    selectedBaseMap,
  ]);


  if (!isLoaded) return null;


  /*-----------------------------------------------------------------------------*/


  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={initialUrlViewport?.center || defaultNZ}
        zoom={initialUrlViewport?.zoom ?? 5}
        options={options}
        onIdle={persistCurrentViewport}
        onLoad={(map) => {

          mapRef.current = map;

          // Fire once, when the map has rendered.
          google.maps.event.addListenerOnce(map, "idle", () => {
            setMapReady(true);
          });
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
      >
      </GoogleMap >

      <TopButtonsStock
        onZoomToAll={handleZoomToAll}
        listingCount={listingCount}
      />
    </>
  );
}

export default forwardRef(StockMapComponent);
